import { URL } from '@api/urls';

import {
	IBranchProgress,
	TCMBranches,
	TCMNodeTypesOptions,
	TCMTechnologiesOptions,
	TCMVendorsOptions,
} from '@interfaces/cmModule/cmModule';
import { IConsistencyCheckRan, IConsistencyCheckRanCheckTemplates, TConsistencyCheckDetails } from '@interfaces/cmModule/consistencyCheck';
import {
	IDumpId,
	TCMConfigTypesDTO,
	TCMDatesMavenirDatesDTO,
	TCMDefaultDTO,
	TCMNodeVendorTechnology,
	TCMVersionsDTO,
	TDetailsOptionsDTO,
} from '@interfaces/cmModule/dto/cmModuleDTO';
import {
	IConsistencyCheckBatchDTO,
	IConsistencyCheckBranchesDTO,
	IConsistencyCheckRanTableDTO,
	TConsistencyCheckAssignTemplateDTO,
	TConsistencyCheckBranchesFetchDTO,
	TConsistencyCheckBranchExportDTO,
	TConsistencyCheckDetailsDTO,
	TConsistencyCheckRanGetAvailableTemplatesDTO,
} from '@interfaces/cmModule/dto/consistencyCheckDTO';
import { IId, TIdName, TNameTitle } from '@interfaces/common/common';
import { IItemsResponse } from '@interfaces/common/response';

import { TVendorsValues } from '@enums/common';

import { client } from './client';

const {
	configurationConsistency,
	configurationConsistencyConfigTypes,
	configurationConsistencyDates,
	configurationConsistencyNodes,
	configurationConsistencyNodeTypes,
	configurationConsistencyParameters,
	configurationConsistencyParametersProgresses,
	configurationConsistencyTechnologies,
	configurationConsistencyVendors,
	configurationConsistencyDatesOptions,
	configurationConsistencyVersions,
	configurationConsistencyAWTGRan,
} = URL;

export const getConsistencyCheckVendors = (): Promise<TCMVendorsOptions[]> => {
	return client.get(configurationConsistencyVendors);
};

export const getConsistencyCheckTechnologies = (vendor: TVendorsValues): Promise<TCMTechnologiesOptions[]> => {
	return client.post(configurationConsistencyTechnologies, { vendor });
};

export const getConsistencyCheckNodeTypes = (params: TCMNodeVendorTechnology): Promise<TCMNodeTypesOptions[]> => {
	return client.post(configurationConsistencyNodeTypes, params);
};

export const getConsistencyCheckNodes = (vendor: TVendorsValues): Promise<string[]> => {
	return client.post(configurationConsistencyNodes, { vendor });
};

export const getConsistencyCheckVersions = (dto: TCMVersionsDTO): Promise<string[]> => {
	return client.post(configurationConsistencyVersions, dto);
};

export const getConsistencyCheckConfigTypes = (dto: TCMConfigTypesDTO): Promise<TNameTitle[]> => {
	return client.post(configurationConsistencyConfigTypes, dto);
};

export const getConsistencyCheckDates = (params: TCMDefaultDTO): Promise<number[]> => {
	return client.post(configurationConsistencyDates, params);
};

export const getConsistencyCheckDateOptions = (params: TCMDatesMavenirDatesDTO): Promise<string[]> => {
	return client.post(configurationConsistencyDatesOptions, params);
};

export const launchConsistencyProgress = (): Promise<string> => {
	return client.post(configurationConsistencyParametersProgresses, {});
};

export const runConsistencyProgress = (dto: IConsistencyCheckBranchesDTO): Promise<void> => {
	return client.post(`${configurationConsistencyParameters}consistency-run`, dto);
};

export const getConsistencyProgressId = (progressId: string): Promise<IBranchProgress> => {
	return client.get(`${configurationConsistencyParametersProgresses}/${progressId}`);
};

export const deleteConsistencyProgressId = (progressId: string): Promise<void> => {
	return client.delete(`${configurationConsistencyParametersProgresses}/${progressId}`);
};

export const generateConsistencyCheckBranches = (dto: IConsistencyCheckBranchesDTO): Promise<void> => {
	return client.post(`${configurationConsistencyParameters}consistency`, dto);
};

export const fetchConsistencyCheckBranches = (dto: TConsistencyCheckBranchesFetchDTO): Promise<TCMBranches> => {
	return client.post(`${configurationConsistencyParameters}consistency/fetch`, dto);
};

export const getConsistencyCheckDetails = (
	comparisonId: string,
	dto: TConsistencyCheckDetailsDTO,
): Promise<TConsistencyCheckDetails> => {
	return client.post(`${configurationConsistencyParameters}${comparisonId}/details`, dto);
};

export const getConsistencyCheckBranchDeltas = (comparisonId: string): Promise<TNameTitle[]> => {
	return client.get(`${configurationConsistencyParameters}${comparisonId}/deltas`);
};

export const getConsistencyCheckBranchParameters = (comparisonId: string): Promise<string[]> => {
	return client.get(`${configurationConsistencyParameters}${comparisonId}/parameters`);
};

export const getConsistencyCheckBranchConfigIds = (comparisonId: string): Promise<string[]> => {
	return client.get(`${configurationConsistencyParameters}${comparisonId}/configIds`);
};

export const getConsistencyCheckBranchGnbIds = (comparisonId: string): Promise<string[]> => {
	return client.get(`${configurationConsistencyParameters}${comparisonId}/gnbIds`);
};

export const getConsistencyCheckBranchSiteIds = (comparisonId: string): Promise<string[]> => {
	return client.get(`${configurationConsistencyParameters}${comparisonId}/siteids`);
};

export const getConsistencyCheckBranchMoNames = (comparisonId: string): Promise<string[]> => {
	return client.get(`${configurationConsistencyParameters}${comparisonId}/managed-objects-names`);
};

export const getConsistencyCheckBranchMoIds = ({ comparisonId, moName }: TDetailsOptionsDTO): Promise<string[]> => {
	return client.get(`${configurationConsistencyParameters}${comparisonId}/managed-objects-ids`,
		{ params: { moName } },
	);
};

export const getConsistencyCheckBranchesExport = (dto: IDumpId): Promise<void> => {
	return client.post(`${configurationConsistency}/branches/export`, dto);
};

export const getConsistencyCheckBranchExport = (dto: TConsistencyCheckBranchExportDTO): Promise<void> => {
	return client.post(`${configurationConsistency}/branch/export`, dto);
};

export const getConsistencyCheckTableRan = (
	params: IConsistencyCheckRanTableDTO,
): Promise<IItemsResponse<IConsistencyCheckRan>> => {
	return client.get(configurationConsistencyAWTGRan, { params });
};

export const assignConsistencyCheckRanTemplate = (dto: TConsistencyCheckAssignTemplateDTO): Promise<void> => {
	return client.post(`${configurationConsistencyAWTGRan}/assign-template`, dto);
};

export const unassignConsistencyCheckRanTemplate = ({ id }: IId): Promise<void> => {
	return client.post(`${configurationConsistencyAWTGRan}/${id}/unassign-template`, {});
};

export const assignManyConsistencyCheckRanTemplate = (dto: IConsistencyCheckBatchDTO): Promise<void> => {
	return client.post(`${configurationConsistencyAWTGRan}/assign-template-many`, dto);
};

export const enableConsistencyCheckRanMonitoring = ({ id }: IId): Promise<void> => {
	return client.post(`${configurationConsistencyAWTGRan}/${id}/enable-monitoring`, {});
};

export const disableConsistencyCheckRanMonitoring = ({ id }: IId): Promise<void> => {
	return client.post(`${configurationConsistencyAWTGRan}/${id}/disable-monitoring`, {});
};

export const enableManyConsistencyCheckRanMonitoring = (
	dto: TConsistencyCheckRanGetAvailableTemplatesDTO,
): Promise<void> => {
	return client.post(`${configurationConsistencyAWTGRan}/enable-monitoring-many`, dto);
};

export const disableManyConsistencyCheckRanMonitoring = (
	dto: TConsistencyCheckRanGetAvailableTemplatesDTO,
): Promise<void> => {
	return client.post(`${configurationConsistencyAWTGRan}/disable-monitoring-many`, dto);
};

export const unassignManyConsistencyCheckRanTemplate = (dto: IConsistencyCheckBatchDTO): Promise<void> => {
	return client.post(`${configurationConsistencyAWTGRan}/unassign-template-many`, dto);
};

export const checkConsistencyCheckRanTemplateAvailable = (
	dto: IConsistencyCheckBatchDTO,
): Promise<IConsistencyCheckRanCheckTemplates> => {
	return client.post(`${configurationConsistencyAWTGRan}/check-template-available`, dto);
};

export const getConsistencyCheckRanTemplateAvailable = (
	dto: TConsistencyCheckRanGetAvailableTemplatesDTO,
): Promise<TIdName[]> => {
	return client.post(`${configurationConsistencyAWTGRan}/get-available-templates`, dto);
};

export const getConsistencyCheckRanAvailableTemplate = ({ id }: IId): Promise<TIdName[]> => {
	return client.get(`${configurationConsistencyAWTGRan}/${id}/get-available-templates`);
};

export const getConsistencyCheckRanAssignedTemplates = (): Promise<TIdName[]> => {
	return client.get(`${configurationConsistencyAWTGRan}/assigned-templates`);
};

export const getConsistencyCheckRanConsistencyStatuses = (): Promise<TNameTitle[]> => {
	return client.get(`${configurationConsistencyAWTGRan}/static/consistency-statuses`);
};

export const getConsistencyCheckRanMonitoringStatuses = (): Promise<TNameTitle[]> => {
	return client.get(`${configurationConsistencyAWTGRan}/static/monitoring-statuses`);
};
